import React, { useEffect, useState } from 'react';
import { Autocomplete, Stack } from '@contentful/f36-components';
import { TagValueSelector } from './TagValueSelector';
import { TestIds } from '../fixtures/constants';
import { DataNotFound } from './ErrorNotFoundData';
import { TagMetadata } from '../clients/TagsMetadataService';
import { TagSelection } from '../model/ApplicationState';

interface TagsSelectorProps {
  onSelectTag: (tagName: string) => void;
  onRemoveTag: (tagName: string) => void;
  onSelectTagValue: (tagName: string, value: boolean) => void;
  selectedTags: TagSelection[];
  tags: TagMetadata[];
  isLoading: boolean;
}

export const TagsSelector = (props: TagsSelectorProps): React.JSX.Element => {
  const [displayedTags, setDisplayedTags] = useState<TagMetadata[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagMetadata[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');

  const removeSelectedTags = (tags: TagMetadata[]) => {
    return tags.filter((item) => props.selectedTags.every((selected) => selected.name !== item.key));
  };

  const hasTagsInitialized = () => {
    return props.tags && props.tags.length > 0;
  };

  useEffect(() => {
    if (!hasTagsInitialized()) {
      setIsDisabled(true);
    } else {
      const tagsWithoutSelection = removeSelectedTags(props.tags);
      setFilteredTags(tagsWithoutSelection);
      setDisplayedTags(tagsWithoutSelection);
      setIsDisabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.tags)]);

  useEffect(() => {
    const updatedFilteredTags = displayedTags.filter((item) =>
      item.label.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredTags(updatedFilteredTags);
  }, [inputValue, displayedTags]);

  const handleSelectTag = (tag: TagMetadata) => {
    const newDisplayedTags = displayedTags.filter((elem) => elem.key !== tag.key);
    setFilteredTags(newDisplayedTags);
    setDisplayedTags(newDisplayedTags);
    props.onSelectTag(tag.key);
  };

  const handleRemoveTag = (tagName: string) => {
    props.onRemoveTag(tagName);
    if (hasTagsInitialized()) {
      const removedTag = props.tags.find((t) => t.key === tagName);
      if (removedTag) {
        const newDisplayedTags = [...displayedTags, removedTag].sort((a, b) => a.label.localeCompare(b.label));
        setFilteredTags(newDisplayedTags);
        setDisplayedTags(newDisplayedTags);
      }
    }
  };

  const handleInputTagChange = (value: string) => {
    setInputValue(value);
  };

  const getTagLabel = (tagName: string): string => {
    return props.tags.find((t) => t.key === tagName)?.label ?? tagName;
  };

  return (
    <>
      <Stack marginBottom="spacingM" data-test-id={TestIds.TAGS_SELECTOR}>
        <Autocomplete
          items={filteredTags}
          itemToString={(item) => item.label}
          renderItem={(item) => item.label}
          onInputValueChange={handleInputTagChange}
          onSelectItem={handleSelectTag}
          textOnAfterSelect="clear"
          closeAfterSelect={false}
          isDisabled={isDisabled}
          isLoading={props.isLoading}
        />
      </Stack>
      {!props.isLoading && !hasTagsInitialized() && <DataNotFound filter={'tags'} />}
      {hasTagsInitialized() &&
        props.selectedTags.map((tag) => (
          <TagValueSelector
            key={tag.name}
            selectedTag={tag}
            tagLabel={getTagLabel(tag.name)}
            onSelectTagValue={props.onSelectTagValue}
            onRemoveTag={handleRemoveTag}
          />
        ))}
    </>
  );
};
